// Lazy boundary for `ShortPostEditor` (SV-136 A8). The short-post box sits atop every home feed tab and
// under every short post, so the TipTap stack must never ride the startup path (rule 27): every TipTap
// import lives in `ShortPostEditor.tsx`, and this module only names it through a code-split `import()`.
// `ShortPostForm` calls `ShortPostEditorLazy.preload()` from the collapsed box's pointerdown/focus, which
// fetches the chunk WITHOUT changing the rendered tree (rule 26); the swap to the editor happens on
// focus, a real state change the member asked for.
//
// Types are re-exported as `import type` only, so importing this module pulls no TipTap code.

import React, { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import { RecoverableIslandFallback } from "@/main/react/RecoverableIslandFallback";
import type { ShortPostEditorProps } from "@/main/react/shortpost/ShortPostEditor";

export type { ShortPostEditorChange, ShortPostEditorProps } from "@/main/react/shortpost/ShortPostEditor";

export const ShortPostEditorChunk = lazyWithRetry(
    () => import("@/main/react/shortpost/ShortPostEditor"),
    "short-post-editor",
);

/** Starts the chunk fetch without rendering anything. Never throws. */
export function preloadShortPostEditor(): void {
    ShortPostEditorChunk.preload();
}

/**
 * `fallback` is what the member sees while the chunk arrives: the host passes its own one-line box, so a
 * keystroke typed during the load is kept rather than lost. A chunk that never arrives costs the box
 * alone (the retry card), never the feed around it — the same containment the Thought composer uses.
 */
export default function ShortPostEditorLazy(props: ShortPostEditorProps & { fallback: React.ReactNode }) {
    const { fallback, ...editorProps } = props;
    return (
        <LazyControlBoundary controlName="short-post-editor" fallback={<RecoverableIslandFallback />}>
            <Suspense fallback={fallback}>
                <ShortPostEditorChunk {...editorProps} />
            </Suspense>
        </LazyControlBoundary>
    );
}
